// src/components/ProviderProfileModal.jsx
import React, { useEffect, useState } from "react";
import { X, MessageSquare, Award, Clock, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

import api from "../api/axios";

export default function ProviderProfileModal({ providerId, onClose }) {
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;

        async function load() {
            if (!providerId) return;

            // 1. Record View (Analytics)
            try {
                api.post(`/analytics/view/${providerId}`).catch(e => console.error("Track view failed", e));
            } catch (e) { /* ignore */ }

            // 2. Fetch Details
            try {
                const res = await api.get(`/analytics/public/${providerId}`);
                if (alive) setProfile(res.data);
            } catch (err) {
                console.error(err);
            } finally {
                if (alive) setLoading(false);
            }
        }

        load();
        return () => { alive = false; };
    }, [providerId]);

    const startChat = async () => {
        try {
            const res = await api.post(`/conversations/start`, {
                providerId: providerId
            });

            navigate(`/chat?cid=${res.data.conversationId}`);
        } catch (err) {
            alert("Cannot start chat");
        }
    };

    if (!profile && !loading) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md overflow-hidden relative">

                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 bg-black/10 hover:bg-black/20 rounded-full transition-colors z-10"
                >
                    <X className="w-5 h-5 text-gray-700 dark:text-gray-200" />
                </button>

                {/* Header / Cover */}
                <div className="h-32 bg-gradient-to-r from-brand-blue to-purple-500"></div>

                {/* Content */}
                <div className="px-6 pb-6 -mt-12 relative">

                    {/* Avatar */}
                    <div className="w-24 h-24 rounded-full bg-white p-1 shadow-lg mb-4">
                        <div className="w-full h-full rounded-full bg-gray-200 flex items-center justify-center text-2xl font-bold text-gray-400 overflow-hidden">
                            {profile?.avatar ? (
                                <img src={profile.avatar} alt={profile.name} className="w-full h-full object-cover" />
                            ) : (
                                profile?.name?.[0] || "?"
                            )}
                        </div>
                    </div>

                    {loading ? (
                        <div className="space-y-3 animate-pulse">
                            <div className="h-6 w-1/2 bg-gray-200 rounded"></div>
                            <div className="h-4 w-3/4 bg-gray-200 rounded"></div>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center gap-2 mb-1">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {profile.name}
                                </h2>
                                {profile.isCertified === false && (
                                    <div className="group relative">
                                        <AlertCircle className="w-5 h-5 text-amber-500/70" />
                                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-56 p-3 bg-gray-900 text-[11px] text-white rounded-xl shadow-2xl z-50 leading-relaxed font-normal">
                                            <span className="font-bold block mb-1">🧘 Gentle Support Focus</span>
                                            This guide offers dedicated general support. For high-risk concerns or crisis situations, we recommend connecting with a Certified Guardian.
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-wrap gap-2 mb-4">
                                {profile.categories?.map(c => (
                                    <span key={c} className="text-xs font-medium text-brand-blue bg-blue-50 px-2 py-1 rounded">
                                        {c}
                                    </span>
                                ))}
                            </div>

                            <div className="text-gray-600 dark:text-gray-300 text-sm mb-6 leading-relaxed">
                                {profile.bio || "This provider has not added a bio yet."}
                            </div>

                            <div className="flex items-center gap-4 text-xs text-gray-500 mb-6 border-t pt-4 border-gray-100 dark:border-gray-700">
                                <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" /> Joined {new Date(profile.memberSince).toLocaleDateString()}
                                </span>
                                {/* Add more stats here later */}
                            </div>

                            <button
                                onClick={startChat}
                                className="w-full py-3 bg-brand-blue text-white rounded-xl font-medium hover:bg-blue-600 transition-colors shadow-lg shadow-blue-500/30 flex items-center justify-center gap-2"
                            >
                                <MessageSquare className="w-5 h-5" /> Start Conversation
                            </button>
                        </>
                    )}
                </div>

            </div>
        </div>
    );
}
